/**
 * Offline copy of what this register sells (T07-22 Dexie `snapshotItems`, server T07-04
 * `GET /pos/snapshot?locationId=&since=&cursor=` NDJSON): at sign-in and every 5 minutes while
 * online, the delta since the last copy is applied (items upserted, tombstones — sold / removed —
 * deleted). The copy only lets the sale screen show a scanned card at once ("確認中…") and the
 * offline rules (T07-33) decide what may be sold; prices always come from the server.
 */
import type { PosClient } from "@tcg/api-client";

import type { PosDatabase, SnapshotItem } from "./db";
import { ApiError } from "./errors";

export const SNAPSHOT_INTERVAL_MS = 5 * 60_000;

interface ItemLine extends Omit<SnapshotItem, "availabilityConfirmedAt"> {
  type: "item";
}

interface TombstoneLine {
  type: "tombstone";
  variantId: string;
  reason?: string;
}

interface PageLine {
  type: "page";
  nextCursor?: string | null;
  hasMore?: boolean;
  watermark?: string | null;
  generatedAt?: string;
}

type Line = ItemLine | TombstoneLine | PageLine;

export function parseNdjson(text: string): Line[] {
  const out: Line[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) {
      continue;
    }
    try {
      const value = JSON.parse(line) as Line;
      if (value && typeof value === "object" && "type" in value) {
        out.push(value);
      }
    } catch {
      // a torn line: skip, the next delta brings it again
    }
  }
  return out;
}

export interface SnapshotResult {
  items: number;
  tombstones: number;
  pages: number;
}

/**
 * Apply the delta since the last finished run.
 *
 * * `since` = the watermark the server sent in the last page trailer of the previous finished
 *   run (T07-04 review: the tablet's clock may run ahead and a late-committing change would be
 *   skipped for good — a sold card still "for sale" in the offline copy). No watermark yet → a
 *   full copy.
 * * a run stops after `maxPages`; when the server still has more, the next run **resumes from
 *   the cursor with the same `since`** — `since` only moves forward once every page was applied.
 */
export async function syncSnapshot(
  api: PosClient,
  db: PosDatabase,
  locationId: string,
  now: () => number = Date.now,
  maxPages = 50,
): Promise<SnapshotResult> {
  const meta = await db.snapshotMeta.get(locationId);
  const since = meta?.since ?? null;
  let cursor: string | null = meta?.cursor ?? null;
  const result: SnapshotResult = { items: 0, tombstones: 0, pages: 0 };
  let watermark = meta?.watermark ?? null;
  let finished = false;
  for (let page = 0; page < maxPages; page += 1) {
    const response = await api.GET("/v1/pos/snapshot", {
      params: {
        query: {
          locationId,
          ...(since ? { since } : {}),
          ...(cursor ? { cursor } : {}),
        },
      },
      parseAs: "text",
    });
    if (response.response.status === 304) {
      finished = true;
      break;
    }
    if (!response.response.ok || typeof response.data !== "string") {
      throw ApiError.fromEnvelope(response.response.status, response.error);
    }
    const lines = parseNdjson(response.data);
    const confirmed = now();
    const puts: SnapshotItem[] = [];
    const deletes: string[] = [];
    let trailer: PageLine | null = null;
    for (const line of lines) {
      if (line.type === "item") {
        const { type: _type, ...item } = line;
        puts.push({ ...item, availabilityConfirmedAt: confirmed });
      } else if (line.type === "tombstone") {
        deletes.push(line.variantId);
      } else {
        trailer = line;
      }
    }
    await db.transaction("rw", db.snapshotItems, async () => {
      if (puts.length) {
        await db.snapshotItems.bulkPut(puts);
      }
      if (deletes.length) {
        await db.snapshotItems.bulkDelete(deletes);
      }
    });
    result.items += puts.length;
    result.tombstones += deletes.length;
    result.pages += 1;
    watermark = trailer?.watermark ?? watermark;
    cursor = trailer?.nextCursor ?? null;
    if (!trailer?.hasMore || !cursor) {
      finished = true;
      break;
    }
  }
  await db.snapshotMeta.put({
    locationId,
    watermark,
    // finished: the server's watermark (or the old `since` when it sent none — a full copy
    // while there never was one); unfinished: the same `since`, resumed from `cursor`
    since: finished ? (watermark ?? since) : since,
    cursor: finished ? null : cursor,
    fetchedAt: now(),
    count: await db.snapshotItems.count(),
  });
  return result;
}

/** How old the offline copy is (ms) — the sale screen warns when it is older than the rule. */
export async function snapshotAge(
  db: PosDatabase,
  locationId: string,
  now = Date.now(),
): Promise<number | null> {
  const meta = await db.snapshotMeta.get(locationId);
  return meta ? now - meta.fetchedAt : null;
}

/**
 * Whole minutes since the copy was fetched (null = no copy yet). Never below 0: the status bar's
 * clock ticks every 15 s, so a copy fetched after its last tick is "newer than now" — the bar said
 * "−1 minutes ago" (found by the T13-30 manual screenshots).
 */
export function minutesSince(
  fetchedAt: number | null | undefined,
  now = Date.now(),
): number | null {
  return typeof fetchedAt === "number" ? Math.max(0, Math.floor((now - fetchedAt) / 60_000)) : null;
}
